import { useState, useEffect } from "react";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import { Plus, Edit2, Trash2, Loader2, Truck, CalendarOff } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const getAuthHeader = () => ({
  Authorization: `Bearer ${localStorage.getItem("token")}`,
});

const dayNames = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];

export default function RoutesTab({ routes, fetchAllData }) {
  const [open, setOpen] = useState(false);
  const [editingRoute, setEditingRoute] = useState(null);
  const [deleteDialog, setDeleteDialog] = useState({ open: false, route: null });
  const [formData, setFormData] = useState({
    name: "",
    delivery_days: [],
    is_pickup: false,
  });
  const [loading, setLoading] = useState(false);

  const toggleDay = (day) => {
    setFormData((prev) => ({
      ...prev,
      delivery_days: prev.delivery_days.includes(day)
        ? prev.delivery_days.filter((d) => d !== day)
        : [...prev.delivery_days, day],
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (formData.delivery_days.length === 0) {
      toast.error("Please select at least one delivery day");
      return;
    }

    setLoading(true);
    try {
      if (editingRoute) {
        await axios.put(`${API}/routes/${editingRoute.id}`, formData, {
          headers: getAuthHeader(),
        });
        toast.success("Route updated");
      } else {
        await axios.post(`${API}/routes`, formData, { headers: getAuthHeader() });
        toast.success("Route created");
      }

      setOpen(false);
      setEditingRoute(null);
      setFormData({ name: "", delivery_days: [], is_pickup: false });
      fetchAllData();
    } catch (error) {
      toast.error("Operation failed");
    } finally {
      setLoading(false);
    }
  };

  const confirmDelete = async () => {
    if (!deleteDialog.route) return;

    setLoading(true);
    try {
      await axios.delete(`${API}/routes/${deleteDialog.route.id}`, { headers: getAuthHeader() });
      toast.success("Route deleted");
      setDeleteDialog({ open: false, route: null });
      fetchAllData();
    } catch (error) {
      const errorMsg = error.response?.data?.detail || "Failed to delete route";
      toast.error(errorMsg, { duration: 5000 });
    } finally {
      setLoading(false);
    }
  };

  const openEditDialog = (route) => {
    setEditingRoute(route);
    setFormData({
      name: route.name,
      delivery_days: route.delivery_days,
      is_pickup: !!route.is_pickup,
    });
    setOpen(true);
  };

  return (
    <div className="space-y-6">
    <Card className="shadow-lg border-0">
      <CardHeader>
        <div className="flex justify-between items-center">
          <div>
            <CardTitle className="text-2xl">Delivery Routes</CardTitle>
            <CardDescription>Manage delivery schedules for different routes</CardDescription>
          </div>
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button
                data-testid="add-route-button"
                onClick={() => {
                  setEditingRoute(null);
                  setFormData({ name: "", delivery_days: [], is_pickup: false });
                }}
                className="bg-brand-navy hover:bg-brand-navy-hover text-white rounded-sm"
              >
                <Plus className="w-4 h-4 mr-2" />
                Add Route
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>{editingRoute ? "Edit Route" : "Add New Route"}</DialogTitle>
                <DialogDescription>
                  {editingRoute ? "Update route details" : "Create a new delivery route"}
                </DialogDescription>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="route-name">Route Name</Label>
                  <Input
                    id="route-name"
                    data-testid="route-name-input"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    placeholder="e.g., Route A (Mon, Thu)"
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label>Delivery Days</Label>
                  <div className="grid grid-cols-2 gap-2">
                    {dayNames.map((day, index) => (
                      <Button
                        key={index}
                        type="button"
                        data-testid={`day-button-${index}`}
                        variant={formData.delivery_days.includes(index) ? "default" : "outline"}
                        onClick={() => toggleDay(index)}
                        className="w-full"
                      >
                        {day}
                      </Button>
                    ))}
                  </div>
                </div>
                <label className="flex items-start gap-3 rounded-md border border-neutral-200 bg-neutral-50 px-3 py-2.5 cursor-pointer hover:bg-neutral-100 transition-colors">
                  <input
                    type="checkbox"
                    data-testid="route-is-pickup-checkbox"
                    checked={!!formData.is_pickup}
                    onChange={(e) => setFormData({ ...formData, is_pickup: e.target.checked })}
                    className="mt-1 h-4 w-4"
                  />
                  <span className="flex-1">
                    <span className="block text-sm font-semibold">Pickup route</span>
                    <span className="block text-xs text-muted-foreground mt-0.5">
                      Orders for hotels on this route default to <strong>Pickup</strong> — a small tag will
                      show on every order in the Orders, Picking, and Delivery views. You can still
                      override this per-order on the order edit dialog.
                    </span>
                  </span>
                </label>
                <Button
                  type="submit"
                  data-testid="submit-route-button"
                  disabled={loading}
                  className="w-full"
                >
                  {loading ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Saving...
                    </>
                  ) : editingRoute ? (
                    "Update Route"
                  ) : (
                    "Create Route"
                  )}
                </Button>
              </form>
            </DialogContent>
          </Dialog>
        </div>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {routes.map((route) => (
            <Card key={route.id} data-testid={`route-card-${route.id}`} className="border-2">
              <CardContent className="p-6">
                <div className="flex justify-between items-start mb-4">
                  <div className="flex items-center gap-2 flex-wrap">
                    <h3 className="font-semibold text-lg">{route.name}</h3>
                    {route.is_pickup && (
                      <Badge
                        data-testid={`route-pickup-badge-${route.id}`}
                        className="bg-orange-100 text-orange-800 border border-orange-300 text-[10px] uppercase tracking-[0.16em] font-bold px-2 py-0.5"
                      >
                        <Truck className="w-3 h-3 mr-1" />
                        Pickup
                      </Badge>
                    )}
                  </div>
                  <div className="flex space-x-2">
                    <Button
                      onClick={() => openEditDialog(route)}
                      data-testid={`edit-route-${route.id}`}
                      variant="outline"
                      size="icon"
                    >
                      <Edit2 className="w-4 h-4" />
                    </Button>
                    <Button
                      onClick={() => setDeleteDialog({ open: true, route })}
                      data-testid={`delete-route-${route.id}`}
                      variant="destructive"
                      size="icon"
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
                <div className="flex flex-wrap gap-2">
                  {route.delivery_days.map((day) => (
                    <Badge key={day} className="bg-blue-100 text-blue-800">
                      {dayNames[day]}
                    </Badge>
                  ))}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      </CardContent>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={deleteDialog.open} onOpenChange={(open) => setDeleteDialog({ open, route: null })}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Route?</AlertDialogTitle>
            <AlertDialogDescription>
              <p className="mb-2">Are you sure you want to delete this route?</p>
              <div className="bg-yellow-50 p-3 rounded border border-yellow-200">
                <p className="font-semibold">{deleteDialog.route?.name}</p>
                <p className="text-sm text-gray-600">
                  Delivery Days: {deleteDialog.route?.delivery_days.map(d => dayNames[d]).join(", ")}
                </p>
              </div>
              <p className="mt-2 text-red-600 font-semibold">This action cannot be undone!</p>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => setDeleteDialog({ open: false, route: null })}>
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmDelete}
              className="bg-red-600 hover:bg-red-700"
              disabled={loading}
            >
              {loading ? "Deleting..." : "Yes, Delete Route"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Card>

    <NoDeliveryDatesCard />
    </div>
  );
}

function NoDeliveryDatesCard() {
  const [dates, setDates] = useState([]);
  const [newDate, setNewDate] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    axios.get(`${API}/no-delivery-dates`, { headers: getAuthHeader() })
      .then((r) => setDates(r.data?.dates || []))
      .catch(() => {});
  }, []);

  const save = async (next) => {
    setSaving(true);
    try {
      const r = await axios.put(`${API}/no-delivery-dates`, { dates: next }, { headers: getAuthHeader() });
      setDates(r.data.dates || []);
      toast.success("No-delivery dates updated");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to save");
    } finally {
      setSaving(false);
    }
  };

  const addDate = () => {
    if (!newDate) return;
    if (dates.includes(newDate)) { toast.error("Date already added"); return; }
    save([...dates, newDate]);
    setNewDate("");
  };

  const fmt = (d) =>
    new Date(d + "T00:00:00").toLocaleDateString("en-US", { weekday: "short", year: "numeric", month: "short", day: "numeric" });

  return (
    <Card className="shadow-lg border-0" data-testid="no-delivery-dates-card">
      <CardHeader>
        <CardTitle className="text-2xl flex items-center gap-2">
          <CalendarOff className="w-5 h-5 text-red-600" /> No-Delivery Dates
        </CardTitle>
        <CardDescription>
          Public holidays or closure days — customers cannot select these dates when placing an order.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex gap-2 max-w-md">
          <Input
            type="date"
            value={newDate}
            onChange={(e) => setNewDate(e.target.value)}
            data-testid="no-delivery-date-input"
          />
          <Button
            onClick={addDate}
            disabled={!newDate || saving}
            className="bg-brand-navy hover:bg-brand-navy-hover text-white rounded-sm"
            data-testid="no-delivery-date-add"
          >
            <Plus className="w-4 h-4 mr-1" /> Add
          </Button>
        </div>
        {dates.length === 0 ? (
          <p className="text-sm text-neutral-400">No blocked dates — all route days are open for ordering.</p>
        ) : (
          <div className="flex flex-wrap gap-2">
            {dates.map((d) => (
              <span
                key={d}
                className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-red-50 border border-red-200 text-sm text-red-800"
                data-testid={`no-delivery-date-${d}`}
              >
                {fmt(d)}
                <button
                  onClick={() => save(dates.filter((x) => x !== d))}
                  disabled={saving}
                  className="text-red-500 hover:text-red-700"
                  data-testid={`no-delivery-date-remove-${d}`}
                  title="Remove"
                >
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              </span>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}